import * as React from 'react';

import type { Order } from '@klareospace/pharos-lib-types';
import { Input } from '@klareospace/pharos-lib-web-utils';

import { formatHandlingUnitQuantity } from '../utils';
import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { ComputableValueCell } from './computable-value-cell';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HandlingUnitQuantityInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly isError: boolean;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidQuantity(value: number): boolean {
  return Number.isFinite(value) && Number.isInteger(value) && value > 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HandlingUnitQuantityInlineEditor({
  fieldState,
  isError,
  lockReason,
  order,
}: HandlingUnitQuantityInlineEditorProps): React.JSX.Element {
  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit<number>({
      buildPatch: (value) => ({ totalHandlingUnitQuantity: value }),
      isValid: isValidQuantity,
      order,
      successKey: 'drawer.edit.content.success',
      value: order.totalHandlingUnitQuantity?.data ?? 0,
    });

  if (isEditing) {
    return (
      <div className="mt-1 flex items-center gap-1">
        <Input
          className="h-7 flex-1 text-sm"
          disabled={isPending}
          min={0}
          onChange={(e): void => setDraft(Number(e.target.value))}
          step="1"
          type="number"
          value={draft}
        />
        <InlineEditActions isPending={isPending} isValid={isValid} onCancel={handleCancel} onConfirm={handleConfirm} />
      </div>
    );
  }

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      <ComputableValueCell
        formatted={formatHandlingUnitQuantity(order.totalHandlingUnitQuantity)}
        isComputed={order.totalHandlingUnitQuantity?.isComputed}
        isError={isError}
      />
    </EditableFieldWrapper>
  );
}
